/**
 * Whether a plan's おすすめ！ box is ticked in a karte form.
 *
 * The form's own entry for the plan (`{ id, recommended }`, written by
 * PlanItem's handleRecommendPlan) is the truth, because that is what gets
 * saved. `plan.recommended` is the fallback for when the form holds no such
 * entry — which is every mount except composing or restoring a karte:
 *  - view-mode mounts pass no form at all;
 *  - editing a saved karte fills `plans` with bare ids and carries the saved
 *    flag on the plan object instead.
 *
 * Ids are compared through isSamePlanId — the same matcher the toggles use.
 */
export const isPlanRecommended = (
  formPlans: any[] | null | undefined,
  plan: { id: number | string; recommended?: boolean },
): boolean => {
  if (Array.isArray(formPlans)) {
    const index = findPlanEntryIndex(formPlans, plan.id);
    if (index !== -1) {
      return !!formPlans[index].recommended;
    }
  }
  return !!plan.recommended;
};

/**
 * True when two plan ids name the same plan. The ONE comparison every read and
 * every write in PlanItem goes through — when the display matched ids one way
 * and the toggles another, a restored `'7386'` next to a catalogue `7386` was
 * read as ticked but toggled by appending a second entry for the same plan.
 */
export const isSamePlanId = (a: number | string | null | undefined, b: number | string | null | undefined): boolean =>
  a !== null && a !== undefined && b !== null && b !== undefined && Number(a) === Number(b);

/**
 * True when the plan is selected: an object entry for it, or a bare id (the
 * shape editing a saved karte uses).
 */
export const isPlanSelected = (formPlans: any[] | null | undefined, planId: number | string): boolean =>
  Array.isArray(formPlans) && formPlans.some((item) => (item && typeof item === 'object'
    ? isSamePlanId(item.id, planId)
    : isSamePlanId(item, planId)));

/**
 * Index of the form's object entry (`{ id, recommended }`) for a plan, or -1.
 * Only object entries: a bare id (editing a saved karte) is not an entry the
 * toggles can update in place — unchanged from the handlers' behaviour.
 */
export const findPlanEntryIndex = (formPlans: any[], planId: number | string): number =>
  formPlans.findIndex((item) => item && typeof item === 'object' && isSamePlanId(item.id, planId));

/** PlanItem's おすすめ！ toggle, as a pure function. */
export const toggleRecommend = (formPlans: any[], planId: number | string): any[] => {
  const index = findPlanEntryIndex(formPlans, planId);
  if (index === -1) {
    return [...formPlans, { id: planId, recommended: true }];
  }
  return [
    ...formPlans.slice(0, index),
    { ...formPlans[index], recommended: !formPlans[index].recommended },
    ...formPlans.slice(index + 1),
  ];
};

/** PlanItem's plan-selection toggle, as a pure function. */
export const toggleSelect = (formPlans: any[], planId: number | string): any[] => {
  const index = findPlanEntryIndex(formPlans, planId);
  if (index === -1) {
    return [...formPlans, { id: planId, recommended: false }];
  }
  return [...formPlans.slice(0, index), ...formPlans.slice(index + 1)];
};
